// The season timelapse on the season summary card: card 07 of mockup round
// 076 (ui-map-interface.md §3.4, §3.6, R1620).
//
// One section at the foot of the card (R2089): THE SEASON · N FRAMES, a
// small map of the frames beside the banners and the cells each holds at
// the frame, and the transport. The section opens on the newest frame, the sea that ended
// the season, and PLAY starts it from day 0.
//
// The card asks for the frames when it opens: the hub still holds the
// season while the scores stand locked, and a held world keeps them for
// the whole rest (chronicle.md §10.3). A season with no frame shows no
// section.
//
// The section is an element the card keeps while it stands. The card
// regenerates the duel and the columns around it, and never this one, so
// a play and a drag survive the card's own 1 s redraw.

import {
  TIMELAPSE_SUMMARY_MAP_PX, TIMELAPSE_TICKS,
} from '../../gen/presentation';
import { timelapse as T } from '../../gen/strings';
import type { Timelapse } from '../../net/timelapse';
import type { GameState } from '../../state';
import { esc } from '../esc';
import { FrameCanvas } from './chart';
import { IntroSting } from './intro';
import {
  dayTicks, everyHours, frameCounts, frameStamp, legendHtml, legendRows, openingFrame, ticksHtml,
} from './model';
import { TimelapsePlayer } from './player';
import { TransportBinding, transportHtml } from './transport';
import { MarkupHost } from '../markuphost';

export interface SummaryTimelapseDeps {
  state: GameState;
  /** Sends the `TimelapseQuery` of the world socket. */
  query: () => void;
  /** True when the last press became a title hold (R1555). */
  titleHeld?: () => boolean;
}

export class SummaryTimelapse {
  /** The section; it is hidden while the season has no frame. */
  readonly el: HTMLElement;
  readonly player = new TimelapsePlayer();
  private readonly canvas = new FrameCanvas(TIMELAPSE_SUMMARY_MAP_PX);
  private readonly binding: TransportBinding;
  private tl: Timelapse | null = null;
  /** The legend of the frame, one host for each markup of the section. */
  private legend: MarkupHost | null = null;
  /** The frame the map drew last, so a step of the sting draws no map. */
  private drawnFrame = -1;
  /** The sting before frame 0, over the small map (R1655). */
  private readonly sting = new IntroSting(this.player, () => this.el.querySelector<HTMLElement>('.tlmap'));

  constructor(private readonly d: SummaryTimelapseDeps) {
    this.el = document.createElement('section');
    this.el.className = 'tlsum';
    this.el.hidden = true;
    this.binding = new TransportBinding(this.el, this.player, {
      stamp: (f) => (this.tl ? frameStamp(this.tl.frames[f]?.tSec ?? 0) : ''),
      held: d.titleHeld,
    });
    this.player.on(() => { this.draw(); this.sting.update(); });
  }

  /** The card opened: show what the store holds, and ask for the frames
   *  of the season so far. */
  open(): void {
    this.tl = null;
    this.sync();
    this.d.query();
  }

  /** The card closed: the play stops. */
  close(): void { this.player.pause(); }

  /** Adopts the store's timelapse when it changed. */
  sync(): void {
    const t = this.d.state.timelapse;
    if (t === this.tl) return;
    const first = this.tl === null;
    this.tl = t !== null && t.frames.length > 0 ? t : null;
    const tl = this.tl;
    if (tl === null) {
      this.el.hidden = true;
      this.el.replaceChildren();
      this.player.setMax(0, false);
      return;
    }
    const last = tl.frames.length - 1;
    this.el.hidden = false;
    this.el.innerHTML = `<h3><span>${esc(T.summary_head({ frames: tl.frames.length }))}</span>`
      + `<span class="sp"></span><span class="m">${esc(T.summary_every({ hours: everyHours(tl) }))}</span></h3>`
      + '<div class="tlb"><div class="tlmap"></div><div class="tlside">'
      // The legend counts the cells a faction holds at the frame, which
      // are not the cells a commander took (R2089).
      + `<div class="tllh">${esc(T.legend_head)}</div><div class="tlleg"></div>`
      + transportHtml(this.player, '')
      + ticksHtml(dayTicks(tl.frames[0].tSec, tl.frames[last].tSec, TIMELAPSE_TICKS))
      + '</div></div>';
    this.el.querySelector('.tlmap')?.append(this.canvas.el);
    this.legend = new MarkupHost(this.el.querySelector<HTMLElement>('.tlleg')!);
    this.drawnFrame = -1;
    // The section opens on the sea that ended the season: the newest frame
    // that holds an owned cell, before the settlement drained it. A later
    // answer keeps a play where it stands.
    this.player.setMax(last, false);
    if (first) {
      this.player.home = openingFrame(tl);
      this.player.seek(this.player.home);
    }
    this.binding.update();
    this.draw();
  }

  /** Draws the frame under the playhead: the map and the legend. */
  private draw(): void {
    const tl = this.tl;
    const f = tl?.frames[this.player.frame];
    if (!tl || !f) return;
    // A step of the sting moves no frame, so the map stands (R1655).
    if (this.player.intro >= 0 && this.player.frame === this.drawnFrame) return;
    this.drawnFrame = this.player.frame;
    this.canvas.draw(f.cells, tl.cols, tl.rows);
    this.legend?.write(legendHtml(legendRows(frameCounts(f.cells), this.d.state.faction), T.legend_mine));
  }
}
